import { CUSTOMER_TIERS } from '../utils/consts';
import { route } from '../utils/routerUtils';
import {
    ABOUT_TO_EXPIRE_DAYS,
    CHECKOUT_URL,
    isAboutToExpire,
    isAboutToMaxCap,
    isCapMaxed,
    isExpired,
    isHighUsage,
    isLowUsage,
    isNoUsage,
    PACING_WARNING_MESSAGES,
    WARNING_TYPES,
} from './consts';

export const getOrgPlanDetailsBadgeText = (tier = '') => {
    const parsedTier = tier.toUpperCase();

    if (parsedTier === CUSTOMER_TIERS.FREE) {
        return `STATIC.PAGES.ACCOUNT_PLAN.TIER.${parsedTier}_STARTER`;
    } else {
        return `STATIC.PAGES.ACCOUNT_PLAN.TIER.${parsedTier}`;
    }
};

const calculateFreeTierUsageVariables = (isPaying, percentUsage) => {
    if (isNoUsage(percentUsage)) {
        const type = WARNING_TYPES.SUCCESS;
        const message = isPaying ? PACING_WARNING_MESSAGES.usage : PACING_WARNING_MESSAGES.noUsage;
        return { type, message };
    }
    if (isLowUsage(percentUsage, true)) {
        return {
            type: WARNING_TYPES.SUCCESS,
            message: PACING_WARNING_MESSAGES.usage,
        };
    }
    if (isHighUsage(percentUsage, true)) {
        return {
            type: WARNING_TYPES.WARNING,
            message: PACING_WARNING_MESSAGES.usage,
        };
    }
    if (isAboutToMaxCap(percentUsage, true)) {
        return {
            type: WARNING_TYPES.ERROR,
            message: PACING_WARNING_MESSAGES.usage,
        };
    }
    if (isCapMaxed(percentUsage)) {
        return {
            type: WARNING_TYPES.ERROR,
            message: PACING_WARNING_MESSAGES.maxed,
        };
    }
    console.error(`${percentUsage} value is not cover in the range, setting defaults`);
    return {
        type: WARNING_TYPES.WARNING,
        message: PACING_WARNING_MESSAGES.usage,
    };
};

const calculateNonFreeTierUsageVariables = percentUsage => {
    if (isAboutToMaxCap(percentUsage, false)) {
        return {
            type: WARNING_TYPES.ERROR,
            message: PACING_WARNING_MESSAGES.usage,
        };
    }
    if (isCapMaxed(percentUsage)) {
        return {
            type: WARNING_TYPES.ERROR,
            message: PACING_WARNING_MESSAGES.maxed,
        };
    }
    return null;
};

export const calculateWarningVariables = (isFreeTier, isPaying, expiresInDays, percentUsage) => {
    if (isExpired(expiresInDays)) {
        return {
            type: WARNING_TYPES.ERROR,
            message: PACING_WARNING_MESSAGES.expired,
        };
    }
    if (isAboutToExpire(expiresInDays)) {
        return {
            type: WARNING_TYPES.ERROR,
            message: PACING_WARNING_MESSAGES.expiring,
        };
    }
    if (isFreeTier) {
        return calculateFreeTierUsageVariables(isPaying, percentUsage);
    }
    return calculateNonFreeTierUsageVariables(percentUsage);
};

const isContractEndingSoon = contractEndDate => {
    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    return (
        (new Date(contractEndDate) - new Date()) / MS_PER_DAY <= ABOUT_TO_EXPIRE_DAYS &&
        new Date(contractEndDate) - new Date() >= 0
    );
};

export const calcProgressPercentage = (value, maxValue) => (maxValue > 0 ? (value / maxValue) * 100 : 0);

export const calcProgressSectionsPercentages = (values, maxValue) => {
    const total = maxValue === Infinity ? values.reduce((sum, value) => sum + value, 0) : maxValue;

    if (total === 0) return values.map(() => 0);

    return values.map(value => calcProgressPercentage(value, total));
};

export const contractPercent = (contractStartDate, contractEndDate) => {
    const now = new Date();
    const total = new Date(contractEndDate) - new Date(contractStartDate);
    const elapsed = now - new Date(contractStartDate);
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
};

export const calculateColorByContractPercent = (percent, contractEndDate) => {
    if (percent >= 85 || isContractEndingSoon(contractEndDate)) {
        return 'var(--attention)';
    }
    if (percent < 50) {
        return 'var(--success1Color)';
    }
    return 'var(--warning1Color)';
};

export const calculateColorByPacingPercent = (percent, isFreeTier) => {
    let colorCss = 'var(--attention)';
    if (isLowUsage(percent, isFreeTier)) {
        colorCss = 'var(--success1Color)';
    } else if (isHighUsage(percent, isFreeTier)) {
        colorCss = 'var(--warning1Color)';
    }
    return colorCss;
};

export const defaultActionOnProductSelected = product => {
    if (product?.product_id && product?.amount) {
        route(CHECKOUT_URL, { price_id: product.product_id, quantity: product.amount });
    }
};

export const canEnablePlanCardCta = (userTier, tier, upgradeRequestedTier, hasAccountPlan) => {
    if (upgradeRequestedTier) {
        return tier === CUSTOMER_TIERS.FREE && hasAccountPlan;
    }
    if (userTier === CUSTOMER_TIERS.FREE) {
        return hasAccountPlan;
    }
    if (userTier === CUSTOMER_TIERS.STANDARD_UNIFIED) {
        return tier === CUSTOMER_TIERS.ENTERPRISE;
    }
    return tier === userTier;
};

export const getOrganizationPlanStartDate = (tier, pacingStartDate, contractStartDate) => {
    if (tier === CUSTOMER_TIERS.FREE) {
        return contractStartDate;
    }
    return pacingStartDate || contractStartDate;
};
